import { useRef, useEffect, useState, useCallback } from 'react';
import './HorizontalSlider.css';

export default function HorizontalSlider({ children, className = '', gap = '0.75rem', scrollToIndex = null }) {
  const containerRef = useRef(null);
  const [showScrollbar, setShowScrollbar] = useState(false);
  const [thumbWidth, setThumbWidth] = useState(0);
  const [thumbLeft, setThumbLeft] = useState(0);

  // Auto-scroll to center the element at scrollToIndex
  useEffect(() => {
    if (scrollToIndex === null || scrollToIndex === undefined) return;

    const container = containerRef.current;
    if (!container) return;

    const childElements = container.children;
    if (!childElements || scrollToIndex >= childElements.length) return;

    const targetElement = childElements[scrollToIndex];
    if (!targetElement) return;

    // Calculate position to center the element
    const containerWidth = container.clientWidth;
    const elementLeft = targetElement.offsetLeft;
    const elementWidth = targetElement.offsetWidth;

    // Center the element in the container
    const scrollPosition = elementLeft - (containerWidth / 2) + (elementWidth / 2);

    container.scrollTo({
      left: scrollPosition,
      behavior: 'smooth'
    });
  }, [scrollToIndex]);

  // Calcular tamaño y posición del thumb
  const updateScrollbar = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;

    const hasOverflow = el.scrollWidth > el.clientWidth;
    setShowScrollbar(hasOverflow);

    if (hasOverflow) {
      const ratio = el.clientWidth / el.scrollWidth;
      setThumbWidth(ratio * 100);
      const scrollRatio = el.scrollLeft / (el.scrollWidth - el.clientWidth);
      setThumbLeft(scrollRatio * (100 - ratio * 100));
    }
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    // Drag scroll logic
    let isDown = false;
    let startX, scrollLeft;

    const onMouseDown = (e) => {
      // Ignorar si el click es en el scrollbar
      if (e.target.closest('.horizontal-slider-track')) return;
      isDown = true;
      el.classList.add('dragging');
      startX = e.pageX - el.offsetLeft;
      scrollLeft = el.scrollLeft;
    };
    const onMouseLeave = () => { isDown = false; el.classList.remove('dragging'); };
    const onMouseUp = () => { isDown = false; el.classList.remove('dragging'); };
    const onMouseMove = (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - el.offsetLeft;
      el.scrollLeft = scrollLeft - (x - startX) * 3;
    };

    el.addEventListener('mousedown', onMouseDown);
    el.addEventListener('mouseleave', onMouseLeave);
    el.addEventListener('mouseup', onMouseUp);
    el.addEventListener('mousemove', onMouseMove);
    el.addEventListener('scroll', updateScrollbar);
    window.addEventListener('resize', updateScrollbar);

    // Initial calculation
    updateScrollbar();

    return () => {
      el.removeEventListener('mousedown', onMouseDown);
      el.removeEventListener('mouseleave', onMouseLeave);
      el.removeEventListener('mouseup', onMouseUp);
      el.removeEventListener('mousemove', onMouseMove);
      el.removeEventListener('scroll', updateScrollbar);
      window.removeEventListener('resize', updateScrollbar);
    };
  }, [updateScrollbar]);

  // Recalcular cuando cambian los children
  useEffect(() => {
    updateScrollbar();
  }, [children, updateScrollbar]);

  // Click en el track para navegar
  const handleTrackClick = (e) => {
    // Ignorar si es click en el thumb
    if (e.target.classList.contains('horizontal-slider-thumb')) return;

    const el = containerRef.current;
    if (!el) return;

    const track = e.currentTarget;
    const rect = track.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickRatio = clickX / rect.width;

    el.scrollLeft = clickRatio * (el.scrollWidth - el.clientWidth);
  };

  // Drag en el thumb
  const handleThumbMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const el = containerRef.current;
    const track = e.target.parentElement;
    if (!el || !track) return;

    const trackRect = track.getBoundingClientRect();
    const startX = e.clientX;
    const startScrollLeft = el.scrollLeft;
    const maxScroll = el.scrollWidth - el.clientWidth;

    const onMouseMove = (moveEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const thumbPixelWidth = (thumbWidth / 100) * trackRect.width;
      const availableSpace = trackRect.width - thumbPixelWidth;
      const deltaRatio = deltaX / availableSpace;
      el.scrollLeft = startScrollLeft + deltaRatio * maxScroll;
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  return (
    <div className="horizontal-slider-wrapper">
      <div ref={containerRef} className={`horizontal-slider ${className}`} style={{ gap }}>
        {children}
      </div>
      {showScrollbar && (
        <div className="horizontal-slider-track" onClick={handleTrackClick}>
          <div
            className="horizontal-slider-thumb"
            style={{ width: `${thumbWidth}%`, left: `${thumbLeft}%` }}
            onMouseDown={handleThumbMouseDown}
          />
        </div>
      )}
    </div>
  );
}
